/*
 * Product toolbar — blocks/product-toolbar.liquid. The bar works as markup
 * (Back is a link; Save and Share aren't shown without scripts); this adds
 * Save and Share. Back is handled by a few lines in the block itself, in place
 * the moment the bar is on screen: a module like this one runs only once the
 * whole page is parsed, and a click on Back before then would have followed
 * the link instead of going back.
 *   - Save: a list of product handles in this browser (localStorage,
 *     "tfe:saved-products", newest first). The label and the bookmark follow
 *     it, other tabs stay in step, and every change is announced to the page
 *     as a "product:saved" event on the document — { handle, saved, handles }
 *     — for whatever lists the saved products.
 *   - Share: the device's share sheet where sharing is what people expect (a
 *     phone, a tablet); elsewhere the product's address is copied, with the
 *     chosen variant, and the label says so for a moment.
 * What Save and Share did is also written to the bar's status region.
 */

const STORAGE_KEY = 'tfe:saved-products';
const MAX_SAVED = 200;
const CONFIRM_MS = 2000;

// Each label rolls under the pointer (snippets/roll-text.liquid), so it holds
// its text twice; both copies change together.
const setLabel = (label, text) => {
  const lines = label.querySelectorAll('.roll__line');
  if (lines.length) lines.forEach((line) => { line.textContent = text; });
  else label.textContent = text;
};

const readSaved = () => {
  try {
    const list = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string') : [];
  } catch (error) {
    return [];
  }
};

/* False when the browser won't keep it (storage blocked, private mode quota). */
const writeSaved = (handles) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX_SAVED)));
    return true;
  } catch (error) {
    return false;
  }
};

const storageWorks = () => {
  try {
    const probe = `${STORAGE_KEY}:probe`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch (error) {
    return false;
  }
};

class ProductToolbar extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onStorage = this.onStorage.bind(this);
  }

  /* Listeners go on here and come off in disconnectedCallback, so the editor
     moving or re-rendering the bar never leaves it listening twice. */
  connectedCallback() {
    this.save = this.querySelector('[data-toolbar-save]');
    this.share = this.querySelector('[data-toolbar-share]');
    this.status = this.querySelector('[data-toolbar-status]');

    if (this.save) {
      /* Nowhere to keep the list: no button, rather than one that forgets. */
      if (storageWorks()) this.renderSaved();
      else this.save.hidden = true;
    }

    this.addEventListener('click', this.onClick);
    window.addEventListener('storage', this.onStorage);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('storage', this.onStorage);
    window.clearTimeout(this.confirmTimer);
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (this.save && target.closest('[data-toolbar-save]')) this.toggleSaved();
    else if (this.share && target.closest('[data-toolbar-share]')) this.shareProduct();
  }

  /* ---- Save ---- */
  isSaved() {
    return readSaved().includes(this.save.dataset.handle);
  }

  renderSaved() {
    const saved = this.isSaved();
    const label = this.save.querySelector('[data-toolbar-label]');
    this.save.classList.toggle('is-saved', saved);
    if (label) setLabel(label, saved ? this.save.dataset.labelSaved : this.save.dataset.labelSave);
  }

  toggleSaved() {
    const handle = this.save.dataset.handle;
    const before = readSaved();
    const saved = !before.includes(handle);
    const handles = saved ? [handle, ...before] : before.filter((item) => item !== handle);
    if (!writeSaved(handles)) {
      this.announce('This browser could not save the product.');
      return;
    }
    this.renderSaved();
    const title = this.save.dataset.title || 'Product';
    this.announce(saved ? `${title} saved.` : `${title} removed from your saved products.`);
    document.dispatchEvent(new CustomEvent('product:saved', { detail: { handle, saved, handles: readSaved() } }));
  }

  /* Another tab saved or removed something. */
  onStorage(event) {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    if (this.save && !this.save.hidden) this.renderSaved();
  }

  /* ---- Share ---- */
  shareUrl() {
    /* The product's own address, plus the variant the visitor is looking at —
       not whatever tracking the address bar arrived with. */
    const url = new URL(this.share.dataset.url || window.location.href, window.location.href);
    const variant = new URL(window.location.href).searchParams.get('variant');
    if (variant) url.searchParams.set('variant', variant);
    return url.toString();
  }

  async shareProduct() {
    const data = { title: this.share.dataset.title || document.title, url: this.shareUrl() };
    const touch = window.matchMedia('(pointer: coarse)').matches;
    if (touch && typeof navigator.share === 'function' && (!navigator.canShare || navigator.canShare(data))) {
      try {
        await navigator.share(data);
      } catch (error) {
        /* Closed without sharing (AbortError), or refused: nothing to report. */
      }
      return;
    }
    if (await this.copy(data.url)) this.confirmCopied();
    else this.announce('The link could not be copied.');
  }

  async copy(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      /* Fall through to the old way. */
    }
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.append(field);
    field.select();
    let copied = false;
    try {
      copied = document.execCommand('copy');
    } catch (error) {
      copied = false;
    }
    field.remove();
    this.share.focus({ preventScroll: true });
    return copied;
  }

  confirmCopied() {
    const label = this.share.querySelector('[data-toolbar-label]');
    if (label) setLabel(label, this.share.dataset.labelCopied);
    this.announce('Link copied.');
    window.clearTimeout(this.confirmTimer);
    this.confirmTimer = window.setTimeout(() => {
      if (label) setLabel(label, this.share.dataset.labelShare);
    }, CONFIRM_MS);
  }

  announce(message) {
    if (!this.status) return;
    /* Emptied first, so the same message said twice is announced twice. */
    this.status.textContent = '';
    window.setTimeout(() => {
      this.status.textContent = message;
    }, 50);
  }
}

if (!customElements.get('product-toolbar')) customElements.define('product-toolbar', ProductToolbar);
